import type { MouseEvent } from 'react'
import { legacyHookAttrs } from '@/msgdeskRuntime/legacyBridge/SelectorContracts'
import { dispatchEditorAction } from '@/msgdeskRuntime/editorActions'
import { contract } from '@/deskUiSkeletons/editor/Editor.helpers'
import type { EditorMode } from '@/msgdeskRuntime/contracts/editorActions'
import { MailboxIcon } from '../MailboxIcons'
import { useEditorNameScopeValidation } from '@/deskUiSkeletons/editor/EditorTemplateContract'

function HiddenSubmit({ legacy, label }: { legacy: Parameters<typeof contract>[0]; label: string }) {
  return <button type="button" {...contract(legacy)} className="!hidden" hidden aria-hidden="true" tabIndex={-1}>{label}</button>
}

export function MailboxEditorActions({
  editorMode,
  settingsOpen,
  onToggleSettings,
  onDiscard,
}: {
  editorMode?: EditorMode
  settingsOpen: boolean
  onToggleSettings: () => void
  onDiscard: (target: HTMLElement) => void
}) {
  const namespace = useEditorNameScopeValidation()
  function handleSave(event: MouseEvent<HTMLButtonElement>) {
    dispatchEditorAction({ action: 'save', target: event.currentTarget, reason: 'mailbox-editor:before-save' })
  }

  function handleDraft(event: MouseEvent<HTMLButtonElement>) {
    dispatchEditorAction({ action: 'save-draft', target: event.currentTarget, reason: 'mailbox-editor:before-save-draft' })
  }

  function handleDiscard(event: MouseEvent<HTMLButtonElement>) {
    onDiscard(event.currentTarget)
  }

  return (
    <div {...legacyHookAttrs('editor.actions', 'flex min-h-14 flex-wrap items-center gap-2 border-b border-[#d9e1e8] bg-[#f8fafb] px-4 py-2')}>
      <button
        type="button"
        {...contract('editor.action.save')}
        className="flex h-9 items-center gap-2 rounded bg-[#2563eb] px-4 text-[12px] font-semibold text-white shadow-sm hover:bg-[#1d4ed8]"
        onClick={handleSave}
        disabled={!namespace.allowed}
      >
        <MailboxIcon name="send" className="h-4 w-4" />
        {editorMode === 'update_msg' ? 'Save changes' : 'Send'}
      </button>
      <button
        type="button"
        {...contract('editor.action.draft')}
        className="flex h-9 items-center gap-2 rounded border border-[#c8d2dc] bg-white px-3 text-[12px] font-medium text-[#314253] shadow-sm hover:bg-[#edf2f6]"
        onClick={handleDraft}
      >
        <MailboxIcon name="edit" className="h-4 w-4" />
        Save draft
      </button>
      <button
        type="button"
        data-md-mailbox-configure="true"
        className={`flex h-9 items-center gap-2 rounded border px-3 text-[12px] font-medium shadow-sm ${settingsOpen ? 'border-[#8eb4e5] bg-[#eaf2ff] text-[#1556a8]' : 'border-[#c8d2dc] bg-white text-[#314253] hover:bg-[#edf2f6]'}`}
        onClick={onToggleSettings}
        aria-expanded={settingsOpen}
      >
        <MailboxIcon name="settings" className="h-4 w-4" />
        Configure
      </button>
      <button
        type="button"
        {...contract('editor.close')}
        className="ml-auto flex h-9 items-center gap-2 rounded px-3 text-[12px] font-medium text-[#6b4b4b] hover:bg-[#f8ecec]"
        onClick={handleDiscard}
      >
        <MailboxIcon name="trash" className="h-4 w-4" />
        Discard
      </button>
      <HiddenSubmit legacy="editor.action.delete" label="Delete" />
      <HiddenSubmit legacy="editor.action.clear" label="Clear" />
      <HiddenSubmit legacy="editor.action.saveAndGo" label="Save and go" />
      <HiddenSubmit legacy="editor.action.restore" label="Restore" />
    </div>
  )
}
